import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Download,
  Film,
  Image,
  Layers3,
  Menu,
  MousePointer2,
  Play,
  Sparkles,
  Wand2,
  X
} from "lucide-react";

// Local before/after images
import livingroomBefore from '../../VirtualStaging/livingroom-before.jpg';
import livingroomAfter from '../../VirtualStaging/livingroom-after.jpg';
import bedroomBefore from '../../VirtualStaging/bedroom-before.png';
import bedroomAfter from '../../VirtualStaging/bedroom-after.png';
import kitchenBefore from '../../VirtualStaging/kitchen-before.png';
import kitchenAfter from '../../VirtualStaging/kitchen-after.jpg';

// MakeWithFlow photo imports
// Interior Photos
import photo01Bathroom from '../../MakeWithFlow/Photos/01-bathroom.jpg';
import photo02Bathroom from '../../MakeWithFlow/Photos/02-bathroom.jpg';
import photo02LivingRoom from '../../MakeWithFlow/Photos/02-living-room.jpg';
import photo03Office from '../../MakeWithFlow/Photos/03-office.jpg';
import photo04Bedroom from '../../MakeWithFlow/Photos/04-bedroom.jpg';
import photo04LivingRoom from '../../MakeWithFlow/Photos/04-living-room.jpg';
import photo05Bedroom from '../../MakeWithFlow/Photos/05-bedroom.jpg';
import photo05LaundryRoom from '../../MakeWithFlow/Photos/05-laundry-room.jpg';
import photo05LivingRoom from '../../MakeWithFlow/Photos/05-living-room.jpg';
import photo06Bedroom from '../../MakeWithFlow/Photos/06-bedroom.jpg';
import photo06GameRoom from '../../MakeWithFlow/Photos/06-game-room.jpg';
import photo06OpenLivingDining from '../../MakeWithFlow/Photos/06-open-living-dining.jpg';
import photo07Kitchen from '../../MakeWithFlow/Photos/07-kitchen.jpg';
import photo07LaundryRoom from '../../MakeWithFlow/Photos/07-laundry-room.jpg';
import photo07Office from '../../MakeWithFlow/Photos/07-office.jpg';
import photo08Bathroom from '../../MakeWithFlow/Photos/08-bathroom.jpg';
import photo08Bedroom from '../../MakeWithFlow/Photos/08-bedroom.jpg';
import photo08LivingRoom from '../../MakeWithFlow/Photos/08-living-room.jpg';
import photo09Bathroom from '../../MakeWithFlow/Photos/09-bathroom.jpg';
import photo09Kitchen from '../../MakeWithFlow/Photos/09-kitchen.jpg';
import photo10Kitchen from '../../MakeWithFlow/Photos/10-kitchen.jpg';
import photo11Office from '../../MakeWithFlow/Photos/11-office.jpg';
import photo12Bedroom from '../../MakeWithFlow/Photos/12-bedroom.jpg';
import photo13Bedroom from '../../MakeWithFlow/Photos/13-bedroom.jpg';
import photo14KidsRoom from '../../MakeWithFlow/Photos/14-kids-room.jpg';
import photo15Bathroom from '../../MakeWithFlow/Photos/15-bathroom.jpg';
import photo16Bathroom from '../../MakeWithFlow/Photos/16-bathroom.jpg';
import photo17GameRoom from '../../MakeWithFlow/Photos/17-game-room.jpg';
import photo18Office from '../../MakeWithFlow/Photos/18-office.jpg';
import photo19Bedroom from '../../MakeWithFlow/Photos/19-bedroom.jpg';
import photo20Bedroom from '../../MakeWithFlow/Photos/20-bedroom.jpg';
import photo21GameRoom from '../../MakeWithFlow/Photos/21-game-room.jpg';
import photo22Bathroom from '../../MakeWithFlow/Photos/22-bathroom.jpg';
import photo23Nursery from '../../MakeWithFlow/Photos/23-nursery.jpg';
import photo24LaundryRoom from '../../MakeWithFlow/Photos/24-laundry-room.jpg';
import photo25Bathroom from '../../MakeWithFlow/Photos/25-bathroom.jpg';
// Exterior Photos
import photo01ExteriorFront from '../../MakeWithFlow/Photos/01-exterior-front.jpg';
import photo02ExteriorFront from '../../MakeWithFlow/Photos/02-exterior-front.jpg';
import photo03ExteriorFront from '../../MakeWithFlow/Photos/03-exterior-front.jpg';
import photo04ExteriorBack from '../../MakeWithFlow/Photos/04-exterior-back.jpg';
import photo26Patio from '../../MakeWithFlow/Photos/26-patio.jpg';
import photo27Patio from '../../MakeWithFlow/Photos/27-patio.jpg';
import photo28ExteriorBack from '../../MakeWithFlow/Photos/28-exterior-back.jpg';
import photo29ExteriorBack from '../../MakeWithFlow/Photos/29-exterior-back.jpg';
import photo30ExteriorBack from '../../MakeWithFlow/Photos/30-exterior-back.jpg';
import photo31ExteriorBack from '../../MakeWithFlow/Photos/31-exterior-back.jpg';
import photo32ExteriorBack from '../../MakeWithFlow/Photos/32-exterior-back.jpg';
import photo33ExteriorBack from '../../MakeWithFlow/Photos/33-exterior-back.jpg';
import photo34ExteriorBack from '../../MakeWithFlow/Photos/34-exterior-back.jpg';
import photo35ExteriorBack from '../../MakeWithFlow/Photos/35-exterior-back.jpg';

// Flow Logo
import flowLogo from '../../Gallery/Flow Logo.png';

// MakeWithFlow videos served from public folder for better performance
const videoReel1 = '/videos/session-reel-modern-living.mp4';
const videoReel2 = '/videos/session-reel-cozy-bedroom.mp4';
const videoReel3 = '/videos/session-reel-reveal-effect.mp4';
const videoReel4 = '/videos/session-reel-smooth-pan.mp4';
const videoReel5 = '/videos/session-reel-kitchen.mp4';
const videoReel6 = '/videos/session-reel-open-concept.mp4';

const MAKEWITHFLOW_VIDEOS = [
  {
    id: 1,
    src: videoReel1,
    title: "Modern Living Room",
    style: "Editorial Style",
    description: "Transform an empty space into a stunning modern living room"
  },
  {
    id: 2,
    src: videoReel2,
    title: "Cozy Bedroom",
    style: "Warm Scandinavian",
    description: "Create an inviting bedroom with natural textures"
  },
  {
    id: 3,
    src: videoReel3,
    title: "Reveal Effect",
    style: "Dramatic Transition",
    description: "Watch the before-after transformation unfold"
  },
  {
    id: 4,
    src: videoReel4,
    title: "Smooth Pan",
    style: "Cinematic Motion",
    description: "A smooth cinematic tour of the staged space"
  },
  {
    id: 5,
    src: videoReel5,
    title: "Contemporary Kitchen",
    style: "Luxury Transitional",
    description: "Elevate your kitchen listing with premium staging"
  },
  {
    id: 6,
    src: videoReel6,
    title: "Open Concept",
    style: "Coastal Calm",
    description: "Bright and airy staging that sells the lifestyle"
  }
];

const MAKEWITHFLOW_PHOTOS = [
  // Bathrooms
  { id: 1, src: photo01Bathroom, title: "Spa Retreat", style: "Luxury Spa", room: "Bathroom" },
  { id: 2, src: photo02Bathroom, title: "Modern Bath", style: "Contemporary", room: "Bathroom" },
  { id: 3, src: photo08Bathroom, title: "Elegant Bath", style: "Classic", room: "Bathroom" },
  { id: 4, src: photo09Bathroom, title: "Minimalist Bath", style: "Modern", room: "Bathroom" },
  { id: 5, src: photo15Bathroom, title: "Fresh Bath", style: "Coastal", room: "Bathroom" },
  { id: 6, src: photo16Bathroom, title: "Serene Bath", style: "Zen", room: "Bathroom" },
  { id: 7, src: photo22Bathroom, title: "Luxury Bath", style: "Resort", room: "Bathroom" },
  { id: 8, src: photo25Bathroom, title: "Clean Lines Bath", style: "Scandinavian", room: "Bathroom" },
  // Living Rooms
  { id: 9, src: photo02LivingRoom, title: "Modern Living", style: "Contemporary", room: "Living Room" },
  { id: 10, src: photo04LivingRoom, title: "Cozy Living", style: "Warm Neutral", room: "Living Room" },
  { id: 11, src: photo05LivingRoom, title: "Bright Living", style: "Airy Modern", room: "Living Room" },
  { id: 12, src: photo06OpenLivingDining, title: "Open Concept", style: "Modern Editorial", room: "Living Room" },
  { id: 13, src: photo08LivingRoom, title: "Elegant Living", style: "Transitional", room: "Living Room" },
  // Bedrooms
  { id: 14, src: photo04Bedroom, title: "Primary Suite", style: "Warm Scandinavian", room: "Bedroom" },
  { id: 15, src: photo05Bedroom, title: "Guest Bedroom", style: "Modern Editorial", room: "Bedroom" },
  { id: 16, src: photo06Bedroom, title: "Serene Bedroom", style: "Zen", room: "Bedroom" },
  { id: 17, src: photo08Bedroom, title: "Cozy Retreat", style: "Warm Neutral", room: "Bedroom" },
  { id: 18, src: photo12Bedroom, title: "Elegant Suite", style: "Classic", room: "Bedroom" },
  { id: 19, src: photo13Bedroom, title: "Modern Bedroom", style: "Contemporary", room: "Bedroom" },
  { id: 20, src: photo19Bedroom, title: "Minimal Bedroom", style: "Minimalist", room: "Bedroom" },
  { id: 21, src: photo20Bedroom, title: "Fresh Bedroom", style: "Coastal", room: "Bedroom" },
  // Offices
  { id: 22, src: photo03Office, title: "Home Office", style: "Professional", room: "Office" },
  { id: 23, src: photo07Office, title: "Creative Studio", style: "Modern", room: "Office" },
  { id: 24, src: photo11Office, title: "Executive Office", style: "Professional", room: "Office" },
  { id: 25, src: photo18Office, title: "Work from Home", style: "Contemporary", room: "Office" },
  // Kitchens
  { id: 26, src: photo07Kitchen, title: "Gourmet Kitchen", style: "Luxury Transitional", room: "Kitchen" },
  { id: 27, src: photo09Kitchen, title: "Chef's Kitchen", style: "Professional", room: "Kitchen" },
  { id: 28, src: photo10Kitchen, title: "Modern Kitchen", style: "Contemporary", room: "Kitchen" },
  // Game Rooms & Entertainment
  { id: 29, src: photo06GameRoom, title: "Game Room", style: "Entertainment", room: "Game Room" },
  { id: 30, src: photo17GameRoom, title: "Media Room", style: "Modern Gaming", room: "Game Room" },
  { id: 31, src: photo21GameRoom, title: "Recreation Room", style: "Fun & Modern", room: "Game Room" },
  // Kids & Nursery
  { id: 32, src: photo14KidsRoom, title: "Kids Room", style: "Playful Modern", room: "Kids Room" },
  { id: 33, src: photo23Nursery, title: "Nursery", style: "Soft & Cozy", room: "Nursery" },
  // Laundry
  { id: 34, src: photo05LaundryRoom, title: "Laundry Room", style: "Clean Modern", room: "Laundry" },
  { id: 35, src: photo07LaundryRoom, title: "Utility Room", style: "Functional", room: "Laundry" },
  { id: 36, src: photo24LaundryRoom, title: "Bright Laundry", style: "Fresh", room: "Laundry" },
  // Exteriors - Front
  { id: 37, src: photo01ExteriorFront, title: "Curb Appeal", style: "Classic", room: "Exterior" },
  { id: 38, src: photo02ExteriorFront, title: "Modern Facade", style: "Contemporary", room: "Exterior" },
  { id: 39, src: photo03ExteriorFront, title: "Luxury Home", style: "Architectural", room: "Exterior" },
  // Patios
  { id: 40, src: photo26Patio, title: "Resort Patio", style: "Outdoor Living", room: "Patio" },
  { id: 41, src: photo27Patio, title: "Entertainment Patio", style: "Al Fresco", room: "Patio" },
  // Exteriors - Back
  { id: 42, src: photo04ExteriorBack, title: "Backyard Oasis", style: "Pool Living", room: "Exterior" },
  { id: 43, src: photo28ExteriorBack, title: "Garden View", style: "Natural", room: "Exterior" },
  { id: 44, src: photo29ExteriorBack, title: "Pool Paradise", style: "Resort", room: "Exterior" },
  { id: 45, src: photo30ExteriorBack, title: "Outdoor Living", style: "Modern", room: "Exterior" },
  { id: 46, src: photo31ExteriorBack, title: "Sunset View", style: "Luxury", room: "Exterior" },
  { id: 47, src: photo32ExteriorBack, title: "Entertainment Deck", style: "Contemporary", room: "Exterior" },
  { id: 48, src: photo33ExteriorBack, title: "Private Retreat", style: "Secluded", room: "Exterior" },
  { id: 49, src: photo34ExteriorBack, title: "Poolside", style: "Coastal", room: "Exterior" },
  { id: 50, src: photo35ExteriorBack, title: "Dream Backyard", style: "Resort Living", room: "Exterior" }
];

const SHOWCASE_IMAGES = [
  {
    id: 1,
    before: livingroomBefore,
    after: livingroomAfter,
    room: "Living Room",
    style: "Modern Editorial",
    accent: "from-emerald-400 to-amber-300"
  },
  {
    id: 2,
    before: bedroomBefore,
    after: bedroomAfter,
    room: "Primary Bedroom",
    style: "Warm Scandinavian",
    accent: "from-amber-300 to-orange-400"
  },
  {
    id: 3,
    before: kitchenBefore,
    after: kitchenAfter,
    room: "Kitchen",
    style: "Luxury Transitional",
    accent: "from-sky-300 to-emerald-300"
  }
];

const PILLARS = [
  {
    icon: Wand2,
    title: "Save $2,000+ Per Listing",
    description: "Traditional staging costs $2,000-$5,000 per property. Flow delivers professional results for a fraction of the cost—stage an entire home for less than the price of renting a single couch."
  },
  {
    icon: Layers3,
    title: "List 14 Days Faster",
    description: "No waiting for furniture delivery, setup crews, or photographer availability. Upload your photos and have staged images ready to publish in under an hour, not days."
  },
  {
    icon: Download,
    title: "Sell 73% Faster",
    description: "Staged homes sell 73% faster than vacant properties. Buyers can finally visualize living in the space, leading to more showings, stronger offers, and quicker closes."
  }
];

const PROOF_POINTS = [
  { value: "30 sec", label: "average first render" },
  { value: "20+ styles", label: "designer-led visual directions" },
  { value: "4K", label: "export-ready delivery" },
  { value: "1 workflow", label: "upload, stage, refine, download" }
];

const TESTIMONIALS = [
  {
    quote: "The rooms feel merchandised, not just decorated. Buyers understand the property faster.",
    author: "Avery Nguyen",
    role: "Luxury Listing Agent"
  },
  {
    quote: "We stopped treating staging like a production bottleneck. It became a fast creative decision.",
    author: "Jordan Patel",
    role: "Broker / Team Lead"
  },
  {
    quote: "The before and after experience alone changes how sellers respond in presentations.",
    author: "Maya Brooks",
    role: "Real Estate Marketing Director"
  }
];

const BeforeAfterSlider = ({ before, after, room, style }) => {
  const [sliderPosition, setSliderPosition] = useState(54);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const updatePosition = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const next = ((clientX - rect.left) / rect.width) * 100;
    setSliderPosition(Math.max(0, Math.min(100, next)));
  };

  useEffect(() => {
    const stopDragging = () => setIsDragging(false);
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("touchend", stopDragging);
    return () => {
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("touchend", stopDragging);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[4/3] overflow-hidden rounded-[2rem] cursor-ew-resize select-none bg-[#0d1420]"
      onMouseDown={() => setIsDragging(true)}
      onMouseMove={(event) => isDragging && updatePosition(event.clientX)}
      onTouchStart={() => setIsDragging(true)}
      onTouchMove={(event) => isDragging && updatePosition(event.touches[0].clientX)}
    >
      <img src={after} alt={`${room} after virtual staging`} className="absolute inset-0 h-full w-full object-cover" draggable={false} />

      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}>
        <img src={before} alt={`${room} before virtual staging`} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      </div>

      <div className="absolute left-5 top-5 rounded-full bg-black/55 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-white backdrop-blur">
        Before
      </div>
      <div className="absolute right-5 top-5 rounded-full bg-[#5eead4]/90 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#0a0f1a] backdrop-blur">
        After
      </div>

      <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
        <div className="rounded-3xl bg-white/18 px-4 py-3 text-white backdrop-blur">
          <p className="text-[11px] uppercase tracking-[0.24em] text-white/65">{room}</p>
          <p className="mt-1 text-lg font-semibold">{style}</p>
        </div>
        <div className="rounded-full bg-black/45 px-4 py-2 text-sm text-white/85 backdrop-blur">
          <MousePointer2 className="mr-2 inline h-4 w-4" />
          Drag to compare
        </div>
      </div>

      <div className="absolute bottom-0 top-0 z-10 w-px bg-[#5eead4]/80" style={{ left: `${sliderPosition}%` }}>
        <div className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#5eead4]/70 bg-[#5eead4] text-[#0a0f1a] shadow-2xl">
          <div className="h-5 w-5 rounded-full bg-[#0a0f1a]" />
        </div>
      </div>
    </div>
  );
};

// Feature dropdown items
const FEATURE_MENU_ITEMS = [
  { label: "AI Virtual Staging", href: "/flow/features/virtual-staging", icon: "🏠", description: "Transform empty rooms with AI" },
  { label: "Video Reels", href: "/flow/features/video-reels", icon: "🎬", description: "Create TikTok & IG reels in seconds" },
  { label: "Batch Processing", href: "/flow/features/batch-processing", icon: "📸", description: "Process multiple photos at once" },
  { label: "Landing Pages", href: "/flow/features/landing-pages", icon: "🌐", description: "Beautiful property galleries" },
  { label: "Agent Portfolio", href: "/flow/features/portfolio", icon: "✨", description: "Your branded agent page" },
  { label: "Refer & Earn", href: "/flow/features/refer-earn", icon: "💰", description: "Earn credits for referrals" }
];

const Navigation = () => {
  const [isSolid, setIsSolid] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setIsSolid(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFeaturesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    ["Features", "#platform"],
    ["How It Works", "#how-it-works"],
    ["Made with Flow", "#makewithflow"],
    ["Pricing", "#pricing"],
    ["Blog", "/flow/blog"]
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -96, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          isSolid ? "border-b border-[#5eead4]/20 bg-[#0a0f1a]/95 shadow-[0_18px_50px_rgba(0,0,0,0.3)] backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <a href="/flow" className="flex items-center gap-3">
            <img src={flowLogo} alt="Flow" className="h-11 w-11 rounded-xl shadow-[0_10px_30px_rgba(20,244,204,0.25)]" />
            <div className="hidden sm:block">
              <div className={`text-lg font-semibold ${isSolid ? "text-white" : "text-white"}`}>Flow</div>
              <div className={`text-[10px] uppercase tracking-[0.18em] ${isSolid ? "text-[#5eead4]" : "text-[#5eead4]/80"}`}>Stage it. Share it. Sell it.</div>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden items-center gap-8 lg:flex">
            {/* Features Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setFeaturesOpen(!featuresOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors ${isSolid ? "text-slate-300 hover:text-white" : "text-white/78 hover:text-white"}`}
              >
                Features
                <ChevronDown className={`h-4 w-4 transition-transform ${featuresOpen ? "rotate-180" : ""}`} />
              </button>

              {/* Dropdown Menu */}
              {featuresOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.96 }}
                  className="absolute left-1/2 top-full mt-3 w-72 -translate-x-1/2 rounded-2xl border border-[#5eead4]/20 bg-[#0d1420] p-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
                >
                  {FEATURE_MENU_ITEMS.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      onClick={() => setFeaturesOpen(false)}
                      className="flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-[#5eead4]/10"
                    >
                      <span className="text-xl">{item.icon}</span>
                      <div>
                        <div className="text-sm font-semibold text-white">{item.label}</div>
                        <div className="text-xs text-slate-400">{item.description}</div>
                      </div>
                    </a>
                  ))}
                  <div className="mt-2 border-t border-[#5eead4]/20 pt-2">
                    <a
                      href="/flow#pricing"
                      onClick={() => setFeaturesOpen(false)}
                      className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold text-[#5eead4] transition-colors hover:bg-[#5eead4]/10"
                    >
                      <ArrowRight className="h-4 w-4" />
                      View Pricing
                    </a>
                  </div>
                </motion.div>
              )}
            </div>

            {[
              ["How It Works", "#how-it-works"],
              ["Made with Flow", "#makewithflow"],
              ["Pricing", "#pricing"],
              ["Blog", "/flow/blog"]
            ].map(([label, href]) => (
              <a
                key={label}
                href={href}
                className={`text-sm font-medium transition-colors ${isSolid ? "text-slate-300 hover:text-white" : "text-white/78 hover:text-white"}`}
              >
                {label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* Start Free Button - visible on all sizes */}
            <a
              href="/?login=true"
              className="hidden sm:block rounded-full bg-[#5eead4] px-5 py-3 text-sm font-semibold text-[#0a0f1a] transition-transform hover:-translate-y-0.5 hover:bg-[#0dd9b5]"
            >
              Start Free
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className={`lg:hidden p-2 rounded-xl transition-colors ${isSolid ? "text-white hover:bg-white/10" : "text-white hover:bg-white/10"}`}
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Drawer */}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: mobileMenuOpen ? 0 : "100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="fixed right-0 top-0 bottom-0 z-[70] w-80 max-w-[85vw] bg-[#0a0f1a] shadow-2xl lg:hidden"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#5eead4]/20">
            <div className="flex items-center gap-3">
              <img src={flowLogo} alt="Flow" className="h-10 w-10 rounded-xl" />
              <div>
                <div className="text-lg font-semibold text-white">Flow</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#5eead4]">Menu</div>
              </div>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation Links */}
          <div className="flex-1 overflow-y-auto py-6">
            {/* Features Section */}
            <div className="px-6 mb-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#5eead4]/60 mb-3">Features</p>
              <div className="space-y-1">
                {FEATURE_MENU_ITEMS.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/80 hover:text-white hover:bg-[#5eead4]/10 transition-colors"
                  >
                    <span className="text-lg">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Main Links */}
            <div className="px-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#5eead4]/60 mb-3">Navigation</p>
              <div className="space-y-1">
                {[
                  ["How It Works", "#how-it-works"],
                  ["Made with Flow", "#makewithflow"],
                  ["Pricing", "#pricing"],
                  ["Blog", "/flow/blog"]
                ].map(([label, href]) => (
                  <a
                    key={label}
                    href={href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center px-4 py-3 rounded-xl text-white/80 hover:text-white hover:bg-[#5eead4]/10 transition-colors font-medium"
                  >
                    {label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Footer CTA */}
          <div className="p-6 border-t border-[#5eead4]/20">
            <a
              href="/?login=true"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full rounded-xl bg-[#5eead4] py-4 text-center text-base font-semibold text-[#0a0f1a] transition-colors hover:bg-[#0dd9b5]"
            >
              Start Free
            </a>
            <p className="mt-3 text-center text-xs text-white/50">
              No credit card required
            </p>
          </div>
        </div>
      </motion.div>
    </>
  );
};

const HeroSection = ({ activeImage, setActiveImage }) => {
  const active = SHOWCASE_IMAGES[activeImage];

  return (
    <section id="top" className="relative overflow-hidden bg-[#0a0f1a] px-6 pb-20 pt-32 text-white md:pb-28 md:pt-36">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(20,244,204,0.12),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(20,244,204,0.08),_transparent_34%)]" />
        <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-[#5eead4]/30 to-transparent" />
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#5eead4]/20 bg-[#5eead4]/10 px-4 py-2 text-sm text-white/80 backdrop-blur"
          >
            <Sparkles className="h-4 w-4 text-[#5eead4]" />
            The Complete AI Marketing Suite
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="mt-8 max-w-4xl text-5xl font-semibold leading-[0.95] tracking-[-0.05em] text-white md:text-7xl"
          >
            Your AI assistant
            <span className="block text-[#5eead4]">for every listing.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            className="mt-8 max-w-2xl text-lg leading-8 text-slate-400 md:text-xl"
          >
            Stage photos. Create video reels. Build landing pages. Capture leads.
            Flow handles your entire listing marketing workflow — from empty room to signed contract.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.24 }}
            className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center"
          >
            <a
              href="/?login=true"
              className="group inline-flex items-center gap-3 rounded-full bg-[#5eead4] px-7 py-4 text-sm font-semibold text-[#0a0f1a] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(20,244,204,0.25)]"
            >
              Start free today
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#platform"
              className="inline-flex items-center gap-3 rounded-full border border-[#5eead4]/20 bg-[#5eead4]/10 px-6 py-4 text-sm font-medium text-white/88 backdrop-blur transition-colors hover:bg-[#5eead4]/20"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#5eead4]/20">
                <Play className="ml-0.5 h-4 w-4 fill-current text-[#5eead4]" />
              </span>
              See the platform
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32 }}
            className="mt-12 grid gap-3 sm:grid-cols-2"
          >
            {[
              { icon: "🏠", text: "AI Virtual Staging in seconds" },
              { icon: "🎬", text: "Video reels for TikTok & IG" },
              { icon: "🌐", text: "Branded landing pages" },
              { icon: "💰", text: "Refer & earn credits" }
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3 rounded-2xl border border-[#5eead4]/10 bg-[#5eead4]/5 px-4 py-3 text-sm text-slate-300">
                <span className="text-lg">{item.icon}</span>
                {item.text}
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          id="showcase"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="relative"
        >
          <div className="absolute -inset-10 rounded-[2.5rem] bg-[radial-gradient(circle,_rgba(20,244,204,0.15),_transparent_52%)] blur-3xl" />
          <div className="relative rounded-[2rem] border border-[#5eead4]/20 bg-[#0d1420]/80 p-4 shadow-[0_40px_100px_rgba(0,0,0,0.4)] backdrop-blur-xl">
            <BeforeAfterSlider before={active.before} after={active.after} room={active.room} style={active.style} />

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {SHOWCASE_IMAGES.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => setActiveImage(index)}
                  className={`overflow-hidden rounded-2xl border text-left transition-all ${
                    activeImage === index
                      ? "border-[#5eead4] bg-[#5eead4]/15 shadow-[0_16px_30px_rgba(20,244,204,0.15)]"
                      : "border-white/8 bg-white/4 hover:border-[#5eead4]/40 hover:bg-[#5eead4]/10"
                  }`}
                >
                  <div className="flex items-center gap-3 p-3">
                    <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${image.accent}`} />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-white">{image.room}</div>
                      <div className="truncate text-xs text-slate-400">{image.style}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const ProofBar = () => (
  <section className="border-y border-[#5eead4]/20 bg-[#0d1420]">
    <div className="mx-auto grid max-w-7xl gap-8 px-6 py-8 sm:grid-cols-2 lg:grid-cols-4">
      {PROOF_POINTS.map((point, index) => (
        <motion.div
          key={point.label}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.08 }}
          className="flex items-center gap-4"
        >
          <div className="text-4xl font-semibold tracking-[-0.05em] text-[#5eead4]">{point.value}</div>
          <div className="text-sm uppercase tracking-[0.18em] text-slate-400">{point.label}</div>
        </motion.div>
      ))}
    </div>
  </section>
);

const WhyFlowSection = () => {
  const platformFeatures = [
    {
      icon: "🏠",
      title: "AI Virtual Staging",
      description: "Transform empty rooms with natural language. Describe what you want — 'modern minimalist' or 'cozy farmhouse' — and watch AI create stunning staged photos in seconds.",
      color: "from-cyan-400 to-teal-500",
      link: "/flow/features/virtual-staging"
    },
    {
      icon: "🎬",
      title: "Video Reels",
      description: "Turn your staged photos into scroll-stopping video reels ready for Instagram, TikTok, and YouTube Shorts. No editing skills required.",
      color: "from-purple-500 to-pink-500",
      link: "/flow/features/video-reels"
    },
    {
      icon: "📸",
      title: "Batch Processing",
      description: "Upload 48 photos at once. AI detects room types automatically, stages everything in parallel, and delivers a complete listing package in minutes.",
      color: "from-emerald-500 to-teal-500",
      link: "/flow/features/batch-processing"
    },
    {
      icon: "🌐",
      title: "Landing Pages",
      description: "Auto-generate stunning property galleries with YOUR branding, YOUR contact info, and built-in lead capture. Every lead goes directly to you—not Zillow.",
      color: "from-blue-500 to-indigo-500",
      link: "/flow/features/landing-pages"
    },
    {
      icon: "✨",
      title: "Agent Portfolio",
      description: "Your branded portfolio page showcasing all active listings. One link for your Instagram bio, email signature, and business cards.",
      color: "from-pink-500 to-rose-500",
      link: "/flow/features/portfolio"
    },
    {
      icon: "💰",
      title: "Refer & Earn",
      description: "Know someone who needs a mortgage? Refer them to our lending team and earn 5,000 credits per closed loan. Bonus milestones for top referrers.",
      color: "from-cyan-400 to-emerald-400",
      link: "/flow/features/refer-earn"
    }
  ];

  return (
    <section id="platform" className="bg-[#0a0f1a] px-6 py-24">
      <div className="mx-auto max-w-7xl">
        <div className="text-center">
          <div className="text-sm font-semibold uppercase tracking-[0.24em] text-[#5eead4]">The Complete Platform</div>
          <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl">
            Everything you need.<br />
            <span className="text-[#5eead4]">All in one place.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-400">
            Flow isn't just virtual staging. It's your complete AI-powered marketing suite—from empty room to signed contract.
          </p>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {platformFeatures.map((feature, index) => (
            <motion.a
              key={feature.title}
              href={feature.link}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="group relative overflow-hidden rounded-3xl border border-[#5eead4]/10 bg-[#0d1420] p-8 backdrop-blur transition-all hover:border-[#5eead4]/30 hover:bg-[#5eead4]/5"
            >
              <div className={`absolute top-0 right-0 h-32 w-32 bg-gradient-to-br ${feature.color} opacity-10 blur-3xl transition-opacity group-hover:opacity-20`} />

              <span className="text-4xl">{feature.icon}</span>

              <h3 className="mt-6 text-xl font-semibold text-white">{feature.title}</h3>

              <p className="mt-3 text-sm leading-7 text-slate-400">{feature.description}</p>

              <div className="mt-6 flex items-center gap-2 text-sm font-medium text-[#5eead4] opacity-0 transition-opacity group-hover:opacity-100">
                Learn more
                <ArrowRight className="h-4 w-4" />
              </div>
            </motion.a>
          ))}
        </div>

        {/* Workflow */}
        <div className="mt-24 rounded-3xl border border-[#5eead4]/10 bg-[#0d1420] p-8 md:p-12">
          <div className="text-center">
            <h3 className="text-2xl font-semibold text-white md:text-3xl">
              The Complete Workflow
            </h3>
            <p className="mt-4 text-slate-400">
              What used to take a weekend now takes 15 minutes
            </p>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-4 md:gap-6">
            {[
              { icon: "📤", label: "Upload Photos" },
              { icon: "🏠", label: "AI Staging" },
              { icon: "🎬", label: "Video Reels" },
              { icon: "🌐", label: "Landing Page" },
              { icon: "📱", label: "Share & Capture" }
            ].map((step, i, arr) => (
              <div key={step.label} className="flex items-center gap-4 md:gap-6">
                <div className="flex flex-col items-center gap-2">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#5eead4]/10 text-2xl">
                    {step.icon}
                  </div>
                  <span className="text-xs font-medium text-slate-300">{step.label}</span>
                </div>
                {i < arr.length - 1 && (
                  <ArrowRight className="h-5 w-5 text-[#5eead4]/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ProofSection = () => (
  <section id="how-it-works" className="bg-[#0d1420] px-6 py-24">
    <div className="mx-auto max-w-7xl">
      {/* Header */}
      <div className="mb-16 text-center">
        <div className="text-sm font-semibold uppercase tracking-[0.24em] text-[#5eead4]">How Flow Works</div>
        <h2 className="mt-4 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl">
          From empty room to market-ready in minutes.
        </h2>
        <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-400">
          Flow is built around one idea: marketing should feel effortless, not overwhelming. Here's how you go from photos to polished content in minutes.
        </p>
      </div>

      {/* Step-by-step story */}
      <div className="mb-20 space-y-12">
        {/* Step 1 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-center"
        >
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-3 rounded-full border border-[#5eead4]/20 bg-[#5eead4]/10 px-4 py-2 text-sm font-semibold text-[#5eead4]">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#5eead4] text-xs text-[#0a0f1a]">1</span>
              Upload Your Photo
            </div>
            <h3 className="mt-6 text-3xl font-semibold tracking-[-0.03em] text-white">
              Start with one empty room
            </h3>
            <p className="mt-4 text-lg leading-8 text-slate-400">
              Drag and drop a single property photo into Flow. The system accepts JPEG, PNG, and WebP files up to 20MB. No batch uploads needed at this stage—Flow is designed for focused, intentional work on one room at a time. The image immediately appears in your workspace, ready for the next step.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {["JPEG", "PNG", "WebP", "Up to 20MB"].map((tag) => (
                <span key={tag} className="rounded-full border border-[#5eead4]/20 bg-[#5eead4]/10 px-3 py-1 text-sm text-slate-300">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="overflow-hidden rounded-[2rem] border border-[#5eead4]/20 bg-[#0a0f1a] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.3)]">
              <div className="flex items-center gap-3 border-b border-[#5eead4]/20 pb-4">
                <div className="h-3 w-3 rounded-full bg-[#e8605a]" />
                <div className="h-3 w-3 rounded-full bg-[#f5c451]" />
                <div className="h-3 w-3 rounded-full bg-[#5dc75d]" />
                <span className="ml-4 text-sm text-slate-500">Flow Workspace</span>
              </div>
              <div className="mt-6 rounded-2xl border-2 border-dashed border-[#5eead4]/30 bg-[#5eead4]/5 p-12 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#5eead4]">
                  <Download className="h-7 w-7 text-[#0a0f1a]" />
                </div>
                <p className="font-medium text-white">Drop your photo here</p>
                <p className="mt-2 text-sm text-slate-500">or click to browse</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Step 2 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-center"
        >
          <div>
            <div className="overflow-hidden rounded-[2rem] border border-[#5eead4]/20 bg-[#0a0f1a] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.3)]">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#5eead4]">Choose a Direction</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {["Modern Editorial", "Warm Scandinavian", "Coastal Calm", "Luxury Transitional"].map((style, i) => (
                  <div key={style} className={`rounded-2xl border p-4 ${i === 0 ? "border-[#5eead4] bg-[#5eead4]/10" : "border-[#5eead4]/10 bg-[#5eead4]/5"}`}>
                    <div className={`mb-3 h-3 w-full rounded-full bg-gradient-to-r ${
                      i === 0 ? "from-cyan-400 to-emerald-400" :
                      i === 1 ? "from-amber-300 to-orange-400" :
                      i === 2 ? "from-sky-300 to-emerald-300" :
                      "from-violet-300 to-rose-300"
                    }`} />
                    <p className={`text-sm font-medium ${i === 0 ? "text-[#5eead4]" : "text-slate-400"}`}>{style}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div className="inline-flex items-center gap-3 rounded-full border border-[#5eead4]/20 bg-[#5eead4]/10 px-4 py-2 text-sm font-semibold text-[#5eead4]">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#5eead4] text-xs text-[#0a0f1a]">2</span>
              Select Room Type & Style
            </div>
            <h3 className="mt-6 text-3xl font-semibold tracking-[-0.03em] text-white">
              Tell Flow what story to create
            </h3>
            <p className="mt-4 text-lg leading-8 text-slate-400">
              Identify the room type—living room, bedroom, kitchen, or one of 20+ other options. Then choose from designer-curated style directions like Modern Editorial, Warm Scandinavian, Coastal Calm, or Luxury Transitional. Each style has been developed to appeal to specific buyer demographics and listing price points.
            </p>
            <p className="mt-4 text-base leading-7 text-slate-500">
              You can also add custom instructions: "warmer wood tones," "add greenery," or "stage for a young professional." Flow interprets your direction and applies it to the generation.
            </p>
          </div>
        </motion.div>

        {/* Step 3 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-center"
        >
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-3 rounded-full border border-[#5eead4]/20 bg-[#5eead4]/10 px-4 py-2 text-sm font-semibold text-[#5eead4]">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#5eead4] text-xs text-[#0a0f1a]">3</span>
              Generate the First Version
            </div>
            <h3 className="mt-6 text-3xl font-semibold tracking-[-0.03em] text-white">
              AI creates the staged image
            </h3>
            <p className="mt-4 text-lg leading-8 text-slate-400">
              Click "Start Staging" and Flow generates your first result in approximately 30 seconds. The AI analyzes the room's architecture, lighting, and proportions, then places furniture, decor, and styling elements that feel intentional—not randomly dropped. The result appears in a before/after slider so you can immediately compare the transformation.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <div className="flex items-center gap-2 rounded-full bg-[#5eead4] px-4 py-2 text-sm text-[#0a0f1a]">
                <Clock3 className="h-4 w-4" />
                ~30 seconds
              </div>
              <span className="text-sm text-slate-500">Average generation time</span>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="overflow-hidden rounded-[2rem] border border-[#5eead4]/20 shadow-[0_18px_50px_rgba(0,0,0,0.3)]">
              <div className="relative aspect-[4/3]">
                <img
                  src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1400&q=80"
                  alt="Staged living room result"
                  className="h-full w-full object-cover"
                />
                <div className="absolute left-4 top-4 rounded-full bg-[#5eead4]/90 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0a0f1a]">
                  Generated Result
                </div>
                <div className="absolute bottom-4 right-4 rounded-full bg-[#0a0f1a] px-4 py-2 text-sm text-[#5eead4]">
                  <Sparkles className="mr-2 inline h-4 w-4" />
                  Modern Editorial
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Step 4 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid gap-8 lg:grid-cols-[1.2fr_1fr] lg:items-center"
        >
          <div className="space-y-4">
            {/* Session Chat UI with Image */}
            <div className="overflow-hidden rounded-[2rem] border border-[#5eead4]/20 bg-[#0a0f1a] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.3)]">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5eead4]">Session</span>
                  <div className="mt-1 text-lg font-semibold text-white">Luxury Transitional Living Room</div>
                </div>
                <span className="rounded-full bg-[#5eead4]/10 px-3 py-1 text-sm text-slate-300">v3 active</span>
              </div>
              <div className="space-y-4">
                <div className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-[#5eead4] px-4 py-3 text-sm text-[#0a0f1a]">
                  Make it brighter, soften the contrast, and add a more upscale conversational seating layout.
                </div>
                <div className="max-w-[90%] rounded-2xl rounded-bl-sm bg-[#5eead4]/10 px-4 py-3 text-sm text-white">
                  Updated the space with warmer daylight, lighter upholstery, a sculptural coffee table, and a cleaner focal arrangement for a luxury listing feel.
                </div>
                <div className="overflow-hidden rounded-[1.5rem] border border-[#5eead4]/20">
                  <img
                    src={SHOWCASE_IMAGES[2].after}
                    alt="Session result preview"
                    className="h-72 w-full object-cover"
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3 rounded-xl bg-[#5eead4]/5 p-3">
                <input type="text" placeholder="Describe the next change..." className="flex-1 bg-transparent text-sm text-slate-300 placeholder-slate-500 outline-none" readOnly />
                <button className="rounded-full bg-[#5eead4] px-4 py-2 text-sm font-semibold text-[#0a0f1a]">Send</button>
              </div>
            </div>
          </div>
          <div>
            <div className="inline-flex items-center gap-3 rounded-full border border-[#5eead4]/20 bg-[#5eead4]/10 px-4 py-2 text-sm font-semibold text-[#5eead4]">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#5eead4] text-xs text-[#0a0f1a]">4</span>
              Refine in Sessions
            </div>
            <h3 className="mt-6 text-3xl font-semibold tracking-[-0.03em] text-white">
              Iterate until the room is perfect
            </h3>
            <p className="mt-4 text-lg leading-8 text-slate-400">
              If the first result isn't quite right, open a Session. Sessions let you refine the image through natural language: "soften the color palette," "add more texture," "make it feel more luxurious." Each refinement builds on the previous version, and you can compare all iterations in the version history.
            </p>
            <p className="mt-4 text-base leading-7 text-slate-500">
              Sessions preserve your creative decisions so you can return to any version later or branch in a different direction.
            </p>
          </div>
        </motion.div>

        {/* Step 5 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-center"
        >
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-3 rounded-full border border-[#5eead4]/20 bg-[#5eead4]/10 px-4 py-2 text-sm font-semibold text-[#5eead4]">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#5eead4] text-xs text-[#0a0f1a]">5</span>
              Download & Deploy
            </div>
            <h3 className="mt-6 text-3xl font-semibold tracking-[-0.03em] text-white">
              Export production-ready assets
            </h3>
            <p className="mt-4 text-lg leading-8 text-slate-400">
              When you're satisfied, download the final image in your choice of resolution—from web-optimized for MLS listings to 4K for print materials and large displays. The exported file is ready to use immediately in listing presentations, marketing brochures, social media, and advertising campaigns.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {[
                { res: "Web", desc: "MLS & social" },
                { res: "HD", desc: "Presentations" },
                { res: "4K", desc: "Print & ads" }
              ].map((opt) => (
                <div key={opt.res} className="rounded-2xl border border-[#5eead4]/20 bg-[#0a0f1a] p-4 text-center">
                  <p className="text-xl font-semibold text-[#5eead4]">{opt.res}</p>
                  <p className="mt-1 text-sm text-slate-500">{opt.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="overflow-hidden rounded-[2rem] border border-[#5eead4]/20 bg-[#0a0f1a] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.3)]">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-400">Ready for export</span>
                <span className="rounded-full bg-[#5eead4]/20 px-3 py-1 text-sm font-medium text-[#5eead4]">Complete</span>
              </div>
              <div className="space-y-3">
                {["living-room-modern-v3.jpg", "living-room-modern-v3-4k.jpg"].map((file) => (
                  <div key={file} className="flex items-center justify-between rounded-xl border border-[#5eead4]/10 bg-[#5eead4]/5 p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#5eead4]">
                        <Download className="h-5 w-5 text-[#0a0f1a]" />
                      </div>
                      <span className="text-sm font-medium text-white">{file}</span>
                    </div>
                    <button className="rounded-full bg-[#5eead4] px-4 py-2 text-sm text-[#0a0f1a]">Download</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Testimonials */}
      <div className="border-t border-[#5eead4]/20 pt-16">
        <div className="mb-10 text-center">
          <div className="text-sm font-semibold uppercase tracking-[0.24em] text-[#5eead4]">What Professionals Say</div>
          <h3 className="mt-4 text-3xl font-semibold tracking-[-0.04em] text-white">
            Real feedback from real estate teams
          </h3>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {TESTIMONIALS.map((item, index) => (
            <motion.div
              key={item.author}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="rounded-[2rem] border border-[#5eead4]/10 bg-[#0a0f1a] p-8 shadow-[0_18px_50px_rgba(0,0,0,0.2)]"
            >
              <p className="text-lg leading-8 text-slate-300">"{item.quote}"</p>
              <div className="mt-8 border-t border-[#5eead4]/10 pt-5">
                <div className="font-semibold text-white">{item.author}</div>
                <div className="text-sm text-slate-500">{item.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const VideoCard = ({ video }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Auto-play briefly to show first frame, then pause
  const handleLoadedData = () => {
    setIsLoaded(true);
    if (videoRef.current) {
      videoRef.current.currentTime = 0.1; // Show first frame
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="group relative overflow-hidden rounded-[2rem] border border-[#5eead4]/10 bg-[#0d1420] shadow-[0_18px_50px_rgba(0,0,0,0.3)]"
    >
      <div className="relative aspect-[9/16] overflow-hidden">
        {/* Loading placeholder */}
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0d1420]">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#5eead4]/20 border-t-[#5eead4]" />
          </div>
        )}
        <video
          ref={videoRef}
          src={video.src}
          className={`h-full w-full object-cover transition-opacity ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          loop
          muted
          playsInline
          preload="auto"
          onLoadedData={handleLoadedData}
          onEnded={() => setIsPlaying(false)}
        />

        {/* Play/Pause Overlay */}
        <button
          onClick={togglePlay}
          className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity ${
            isPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'
          }`}
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#5eead4]/20 backdrop-blur-sm transition-transform hover:scale-110">
            {isPlaying ? (
              <div className="flex gap-1">
                <div className="h-6 w-2 rounded-sm bg-[#5eead4]" />
                <div className="h-6 w-2 rounded-sm bg-[#5eead4]" />
              </div>
            ) : (
              <Play className="ml-1 h-8 w-8 fill-[#5eead4] text-[#5eead4]" />
            )}
          </div>
        </button>
      </div>

      {/* Video Info */}
      <div className="p-5">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#5eead4]/10 px-3 py-1">
          <Sparkles className="h-3 w-3 text-[#5eead4]" />
          <span className="text-xs font-medium text-[#5eead4]">{video.style}</span>
        </div>
        <h4 className="text-lg font-semibold text-white">{video.title}</h4>
        <p className="mt-1 text-sm text-slate-400">{video.description}</p>
      </div>
    </motion.div>
  );
};

const PhotoCard = ({ photo }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative overflow-hidden rounded-[1.5rem] border border-[#5eead4]/10 bg-[#0d1420] shadow-[0_18px_50px_rgba(0,0,0,0.3)]"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={photo.src}
          alt={photo.title}
          className={`h-full w-full object-cover transition-transform duration-700 ${
            isHovered ? 'scale-105' : 'scale-100'
          }`}
        />

        {/* Clean image - no overlays */}
      </div>
    </motion.div>
  );
};

const MakeWithFlowSection = () => {
  const [activeTab, setActiveTab] = useState('photos');

  // Split photos into two rows for marquee effect
  const photosRow1 = MAKEWITHFLOW_PHOTOS.slice(0, Math.ceil(MAKEWITHFLOW_PHOTOS.length / 2));
  const photosRow2 = MAKEWITHFLOW_PHOTOS.slice(Math.ceil(MAKEWITHFLOW_PHOTOS.length / 2));

  // Split videos into two rows
  const videosRow1 = MAKEWITHFLOW_VIDEOS.slice(0, Math.ceil(MAKEWITHFLOW_VIDEOS.length / 2));
  const videosRow2 = MAKEWITHFLOW_VIDEOS.slice(Math.ceil(MAKEWITHFLOW_VIDEOS.length / 2));

  return (
    <section id="makewithflow" className="bg-[#0a0f1a] py-24 overflow-hidden">
      {/* Marquee CSS */}
      <style>{`
        @keyframes marquee-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marquee-right {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .marquee-track {
          display: flex;
          width: max-content;
        }
        .marquee-left {
          animation: marquee-left 180s linear infinite;
        }
        .marquee-right {
          animation: marquee-right 180s linear infinite;
        }
        .marquee-container:hover .marquee-track {
          animation-play-state: paused;
        }
      `}</style>

      <div className="mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="mb-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 rounded-full border border-[#5eead4]/20 bg-[#5eead4]/10 px-4 py-2 text-sm text-white/80 backdrop-blur"
          >
            <Sparkles className="h-4 w-4 text-[#5eead4]" />
            Showcase
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-6 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl"
          >
            Made with <span className="text-[#5eead4]">Flow</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-400"
          >
            See the magic in action. Staged photos and video reels created entirely with Flow's AI marketing tools.
          </motion.p>
        </div>

        {/* Tabs */}
        <div className="mb-10 flex justify-center">
          <div className="inline-flex rounded-full border border-[#5eead4]/20 bg-[#5eead4]/5 p-1.5">
            <button
              onClick={() => setActiveTab('reels')}
              className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all ${
                activeTab === 'reels'
                  ? 'bg-[#5eead4] text-[#0a0f1a]'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              <Film className="h-4 w-4" />
              Reels
            </button>
            <button
              onClick={() => setActiveTab('photos')}
              className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all ${
                activeTab === 'photos'
                  ? 'bg-[#5eead4] text-[#0a0f1a]'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              <Image className="h-4 w-4" />
              Photos
            </button>
          </div>
        </div>
      </div>

      {/* Marquee Content - Full Width */}
      {activeTab === 'reels' ? (
        <motion.div
          key="reels"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {/* Row 1 - Scroll Left */}
          <div className="marquee-container">
            <div className="marquee-track marquee-left">
              {[...videosRow1, ...videosRow1].map((video, idx) => (
                <div key={`r1-${video.id}-${idx}`} className="mx-3 w-[350px] flex-shrink-0">
                  <VideoCard video={video} />
                </div>
              ))}
            </div>
          </div>
          {/* Row 2 - Scroll Right */}
          <div className="marquee-container">
            <div className="marquee-track marquee-right">
              {[...videosRow2, ...videosRow2].map((video, idx) => (
                <div key={`r2-${video.id}-${idx}`} className="mx-3 w-[350px] flex-shrink-0">
                  <VideoCard video={video} />
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="photos"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {/* Row 1 - Scroll Left */}
          <div className="marquee-container">
            <div className="marquee-track marquee-left">
              {[...photosRow1, ...photosRow1].map((photo, idx) => (
                <div key={`r1-${photo.id}-${idx}`} className="mx-3 w-[350px] flex-shrink-0">
                  <PhotoCard photo={photo} />
                </div>
              ))}
            </div>
          </div>
          {/* Row 2 - Scroll Right */}
          <div className="marquee-container">
            <div className="marquee-track marquee-right">
              {[...photosRow2, ...photosRow2].map((photo, idx) => (
                <div key={`r2-${photo.id}-${idx}`} className="mx-3 w-[350px] flex-shrink-0">
                  <PhotoCard photo={photo} />
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* CTA */}
      <div className="mx-auto max-w-7xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <a
            href="/?login=true"
            className="group inline-flex items-center gap-3 rounded-full bg-[#5eead4] px-7 py-4 text-sm font-semibold text-[#0a0f1a] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(20,244,204,0.25)]"
          >
            Create your own {activeTab === 'reels' ? 'videos' : 'photos'}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const USER_TESTIMONIALS = [
  {
    id: 1,
    name: "Sarah Mitchell",
    role: "Luxury Real Estate Agent",
    company: "Coldwell Banker",
    quote: "Flow has completely transformed how I present listings. My clients are amazed when they see their empty properties come to life. I've closed 3 more deals this quarter alone!",
    avatar: "SM",
    highlight: "3 more deals this quarter"
  },
  {
    id: 2,
    name: "Michael Chen",
    role: "Real Estate Photographer",
    company: "Vista Property Media",
    quote: "As a photographer, I was skeptical about AI staging. But Flow's quality is incredible - it saves me hours of work and my clients can't tell the difference from traditional staging.",
    avatar: "MC",
    highlight: "saves me hours of work"
  },
  {
    id: 3,
    name: "Jennifer Rodriguez",
    role: "Broker & Team Lead",
    company: "RE/MAX Elite",
    quote: "We've reduced our staging costs by 80% since switching to Flow. The ROI is unbelievable, and our listings sell faster than ever. It's now standard practice for our entire team.",
    avatar: "JR",
    highlight: "reduced staging costs by 80%"
  },
  {
    id: 4,
    name: "David Thompson",
    role: "Property Developer",
    company: "Horizon Developments",
    quote: "Before Flow, we'd wait weeks for staging. Now we can market properties the same day we photograph them. The video reels have been a game-changer for our social media presence.",
    avatar: "DT",
    highlight: "market properties the same day"
  },
  {
    id: 5,
    name: "Amanda Foster",
    role: "Interior Designer",
    company: "Foster Design Studio",
    quote: "I use Flow to show clients design possibilities before committing to furniture. The style options are spot-on and help close design consultations faster.",
    avatar: "AF",
    highlight: "close consultations faster"
  },
  {
    id: 6,
    name: "Robert Kim",
    role: "Real Estate Investor",
    company: "Prime Properties LLC",
    quote: "Flow helps me visualize renovation potential instantly. I can stage a property virtually, show investors the vision, and secure funding faster. Essential tool for flippers.",
    avatar: "RK",
    highlight: "secure funding faster"
  }
];

const TestimonialCard = ({ testimonial }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="flex-shrink-0 w-[350px] rounded-2xl border border-[#5eead4]/10 bg-[#0d1420] p-6 shadow-[0_8px_30px_rgba(0,0,0,0.3)]"
  >
    {/* Header with Avatar */}
    <div className="flex items-center gap-4 mb-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#5eead4] to-[#0dd9b5] text-sm font-bold text-[#0a0f1a]">
        {testimonial.avatar}
      </div>
      <div>
        <h4 className="font-semibold text-white">{testimonial.name}</h4>
        <p className="text-sm text-slate-400">{testimonial.role}</p>
        <p className="text-xs text-slate-500">{testimonial.company}</p>
      </div>
    </div>

    {/* Quote */}
    <p className="text-slate-300 leading-relaxed mb-4">
      "{testimonial.quote}"
    </p>

    {/* Highlight */}
    <div className="inline-flex items-center gap-2 rounded-full bg-[#5eead4]/10 px-3 py-1.5">
      <Sparkles className="h-3.5 w-3.5 text-[#5eead4]" />
      <span className="text-sm font-medium text-[#5eead4]">{testimonial.highlight}</span>
    </div>
  </motion.div>
);

const UserTestimonialsSection = () => (
  <section id="testimonials" className="bg-[#0d1420] px-6 py-24 overflow-hidden">
    <div className="mx-auto max-w-7xl">
      {/* Header */}
      <div className="mb-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 rounded-full border border-[#5eead4]/20 bg-[#5eead4]/10 px-4 py-2 text-sm font-semibold text-[#5eead4]"
        >
          TESTIMONIALS
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="mt-6 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl"
        >
          What our users are saying
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 }}
          className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-400"
        >
          Real estate professionals, photographers, and agents are using Flow every day to boost their listings and close more deals.
        </motion.p>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-8"
        >
          <a
            href="/?login=true"
            className="group inline-flex items-center gap-3 rounded-full bg-[#5eead4] px-7 py-4 text-sm font-semibold text-[#0a0f1a] transition-all hover:-translate-y-0.5 hover:bg-[#0dd9b5]"
          >
            Start for free
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>

      {/* Testimonials Carousel */}
      <div className="relative">
        {/* Gradient Edges */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#0d1420] to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#0d1420] to-transparent z-10" />

        {/* Scrolling Container */}
        <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide px-4 -mx-4 snap-x snap-mandatory">
          {USER_TESTIMONIALS.map((testimonial) => (
            <div key={testimonial.id} className="snap-start">
              <TestimonialCard testimonial={testimonial} />
            </div>
          ))}
        </div>
      </div>

      {/* Stats Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-16 grid gap-8 sm:grid-cols-3 text-center"
      >
        <div>
          <div className="text-4xl font-bold text-[#5eead4]">10,000+</div>
          <div className="mt-2 text-sm text-slate-400">Properties Staged</div>
        </div>
        <div>
          <div className="text-4xl font-bold text-[#5eead4]">4.9/5</div>
          <div className="mt-2 text-sm text-slate-400">Average Rating</div>
        </div>
        <div>
          <div className="text-4xl font-bold text-[#5eead4]">73%</div>
          <div className="mt-2 text-sm text-slate-400">Faster Sales</div>
        </div>
      </motion.div>
    </div>
  </section>
);

const CTASection = () => (
  <section className="bg-[#0a0f1a] px-6 py-24 text-white">
    <div className="mx-auto max-w-6xl">
      <div className="relative overflow-hidden rounded-[2.5rem] border border-[#5eead4]/20 bg-[linear-gradient(135deg,#0d1420_0%,#0a0f1a_38%,#0d2a2a_100%)] px-8 py-14 shadow-[0_40px_100px_rgba(0,0,0,0.4)] md:px-14 md:py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(20,244,204,0.15),_transparent_26%),radial-gradient(circle_at_bottom_right,_rgba(20,244,204,0.1),_transparent_30%)]" />
        <div className="relative z-10 max-w-3xl">
          <div className="text-sm font-semibold uppercase tracking-[0.24em] text-[#5eead4]">Start now</div>
          <h2 className="mt-5 text-4xl font-semibold tracking-[-0.05em] text-white md:text-6xl">
            Turn one empty room into the image that wins the listing.
          </h2>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-400">
            Flow should feel like a premium marketing advantage from the first interaction. Start with one photo and see how quickly the story changes.
          </p>
          <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <a
              href="/"
              className="group inline-flex items-center gap-3 rounded-full bg-[#5eead4] px-7 py-4 text-sm font-semibold text-[#0a0f1a] transition-all hover:-translate-y-0.5"
            >
              Get started free
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <div className="inline-flex items-center gap-3 rounded-full border border-[#5eead4]/20 bg-[#5eead4]/10 px-5 py-4 text-sm text-slate-300">
              <Sparkles className="h-4 w-4 text-[#5eead4]" />
              No furniture rental. No waiting on production.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const PricingSection = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const prices = {
    starter: billingPeriod === 'yearly' ? '$29.99' : '$39.99',
    pro: billingPeriod === 'yearly' ? '$74' : '$99'
  };

  return (
  <section id="pricing" className="bg-[#0a0f1a] px-6 py-24">
    <div className="mx-auto max-w-7xl">
      {/* Header */}
      <div className="mb-16 text-center">
        <div className="text-sm font-semibold uppercase tracking-[0.24em] text-[#5eead4]">Pricing</div>
        <h2 className="mt-4 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl">
          Simple Pricing for AI-Powered Teams
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-400">
          Get started with Flow and transform your business today.
        </p>

        {/* Billing Toggle */}
        <div className="mt-8 flex items-center justify-center">
          <div className="inline-flex rounded-full border-2 border-[#5eead4]/30 bg-[#0d1420] p-1">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`rounded-full px-6 py-2.5 text-sm font-semibold uppercase tracking-wider transition-all ${
                billingPeriod === 'monthly'
                  ? 'bg-[#5eead4] text-[#0a0f1a]'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold uppercase tracking-wider transition-all ${
                billingPeriod === 'yearly'
                  ? 'bg-[#5eead4] text-[#0a0f1a]'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Yearly
              <span className="rounded-full bg-[#0a0f1a] px-2 py-0.5 text-xs font-bold text-[#5eead4]">
                25% OFF
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-3">
        {/* Free Plan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-[2rem] border border-[#5eead4]/10 bg-[#0d1420] p-8"
        >
          {/* Plan Name & Price */}
          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-white">Free</h3>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-5xl font-bold text-white">$0</span>
              <span className="text-lg text-slate-400">/month</span>
            </div>
            <p className="mt-2 text-slate-400">Get started and explore Flow</p>
          </div>

          {/* Credits Highlight */}
          <div className="mb-8 rounded-2xl border border-[#5eead4]/10 bg-[#5eead4]/5 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-400">One-Time Credits</p>
                <p className="mt-1 text-3xl font-bold text-white">5,000</p>
                <p className="mt-1 text-sm text-slate-500">≈ 66 HD photos</p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#5eead4]/10">
                <Sparkles className="h-7 w-7 text-slate-400" />
              </div>
            </div>
          </div>

          {/* AI Virtual Staging Section */}
          <div className="mb-6 space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wider text-slate-400">AI Virtual Staging</p>
            {[
              "Single photo staging only",
              "All room types & styles",
              "No watermark",
              "Commercial license"
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#5eead4]/10">
                  <CheckCircle2 className="h-3.5 w-3.5 text-slate-400" />
                </div>
                <span className="text-white">{feature}</span>
              </div>
            ))}
          </div>

          {/* Limited AI Features */}
          <div className="mb-6 space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wider text-slate-500">AI Marketing Suite</p>
            {[
              { feature: "Video Reels", included: false },
              { feature: "Batch Processing", included: false },
              { feature: "Property Landing Pages", included: false },
              { feature: "Agent Portfolio", included: false },
              { feature: "Refer & Earn", included: false }
            ].map(({ feature, included }) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-800">
                  <X className="h-3.5 w-3.5 text-slate-600" />
                </div>
                <span className="text-slate-600">{feature}</span>
              </div>
            ))}
          </div>

          {/* Platform Features */}
          <div className="mb-8 space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wider text-slate-400">Platform</p>
            {[
              "Leads Management",
              "Note Management",
              "Team: Join only"
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#5eead4]/10">
                  <CheckCircle2 className="h-3.5 w-3.5 text-slate-400" />
                </div>
                <span className="text-white">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <a
            href="/?login=true"
            className="block w-full rounded-2xl border border-[#5eead4]/20 bg-[#5eead4]/10 py-4 text-center text-lg font-semibold text-white transition-all hover:bg-[#5eead4]/20"
          >
            Start Free
          </a>
        </motion.div>

        {/* Starter Plan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="relative overflow-hidden rounded-[2rem] border border-[#5eead4]/30 bg-gradient-to-b from-[#0d1420] to-[#0a0f1a] p-8 shadow-2xl"
        >
          {/* Popular Badge */}
          <div className="absolute right-6 top-6">
            <div className="rounded-full bg-[#5eead4] px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#0a0f1a]">
              Popular
            </div>
          </div>

          {/* Plan Name & Price */}
          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-white">Starter</h3>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-5xl font-bold text-white">{prices.starter}</span>
              <span className="text-lg text-slate-400">/month</span>
            </div>
            <p className="mt-2 text-slate-400">Perfect for growing realty teams</p>
          </div>

          {/* Credits Highlight */}
          <div className="mb-8 rounded-2xl border border-[#5eead4]/20 bg-[#5eead4]/10 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#5eead4]">Monthly Credits</p>
                <p className="mt-1 text-3xl font-bold text-white">15,000</p>
                <p className="mt-1 text-sm text-[#5eead4]/80">≈ 200 HD photos</p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#5eead4]/20">
                <Sparkles className="h-7 w-7 text-[#5eead4]" />
              </div>
            </div>
          </div>

          {/* AI Marketing Suite */}
          <div className="mb-6 space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#5eead4]">AI Marketing Suite</p>
            {[
              "AI Virtual Staging (indoor & outdoor)",
              "Video Reels for TikTok & Instagram",
              "Batch Processing (full photoshoots)",
              "Property Landing Pages",
              "Agent Portfolio Page",
              "Refer & Earn Program"
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#5eead4]/20">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#5eead4]" />
                </div>
                <span className="text-white">{feature}</span>
              </div>
            ))}
          </div>

          {/* Staging Features */}
          <div className="mb-6 space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#5eead4]">Staging Features</p>
            {[
              "All room types & design styles",
              "Natural language refinements",
              "Upscale to 2K & 4K resolution",
              "Commercial photo license",
              "No watermarks"
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#5eead4]/20">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#5eead4]" />
                </div>
                <span className="text-white">{feature}</span>
              </div>
            ))}
          </div>

          {/* Platform Features */}
          <div className="mb-8 space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#5eead4]">Platform</p>
            {[
              "Leads & Notes Management",
              "Team Chat & Collaboration",
              "Team: Up to 3 users",
              "AI Daily Reports"
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#5eead4]/20">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#5eead4]" />
                </div>
                <span className="text-white">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <a
            href="/?login=true"
            className="block w-full rounded-2xl bg-[#5eead4] py-4 text-center text-lg font-semibold text-[#0a0f1a] transition-all hover:bg-[#0dd9b5] hover:shadow-lg"
          >
            Get Started
          </a>

        </motion.div>

        {/* Pro Plan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="relative overflow-hidden rounded-[2rem] border border-purple-500/30 bg-gradient-to-b from-[#0d1420] to-[#0a0f1a] p-8 shadow-2xl"
        >
          {/* Pro Badge */}
          <div className="absolute right-6 top-6">
            <div className="rounded-full bg-purple-500 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white">
              Pro
            </div>
          </div>

          {/* Plan Name & Price */}
          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-white">Pro</h3>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-5xl font-bold text-white">{prices.pro}</span>
              <span className="text-lg text-slate-400">/month</span>
            </div>
            <p className="mt-2 text-slate-400">For elite realty teams ready to scale</p>
          </div>

          {/* Credits Highlight */}
          <div className="mb-8 rounded-2xl border border-purple-500/20 bg-purple-500/10 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-400">Monthly Credits</p>
                <p className="mt-1 text-3xl font-bold text-white">35,000</p>
                <p className="mt-1 text-sm text-purple-400/80">≈ 466 HD photos</p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-500/20">
                <Sparkles className="h-7 w-7 text-purple-400" />
              </div>
            </div>
          </div>

          {/* AI Marketing Suite */}
          <div className="mb-6 space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wider text-purple-400">AI Marketing Suite</p>
            {[
              "AI Virtual Staging (indoor & outdoor)",
              "Video Reels for TikTok & Instagram",
              "Batch Processing (full photoshoots)",
              "Property Landing Pages",
              "Agent Portfolio Page",
              "Refer & Earn Program"
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-500/20">
                  <CheckCircle2 className="h-3.5 w-3.5 text-purple-400" />
                </div>
                <span className="text-white">{feature}</span>
              </div>
            ))}
          </div>

          {/* Staging Features */}
          <div className="mb-6 space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wider text-purple-400">Staging Features</p>
            {[
              "All room types & design styles",
              "Natural language refinements",
              "Upscale to 2K & 4K resolution",
              "Commercial photo license",
              "No watermarks"
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-500/20">
                  <CheckCircle2 className="h-3.5 w-3.5 text-purple-400" />
                </div>
                <span className="text-white">{feature}</span>
              </div>
            ))}
          </div>

          {/* Platform Features */}
          <div className="mb-8 space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wider text-purple-400">Platform</p>
            {[
              "Leads & Notes Management",
              "Team Chat & Collaboration",
              "Team: Up to 10 users",
              "AI Daily Reports",
              "Priority Support"
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-500/20">
                  <CheckCircle2 className="h-3.5 w-3.5 text-purple-400" />
                </div>
                <span className="text-white">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <a
            href="/?login=true"
            className="block w-full rounded-2xl bg-purple-500 py-4 text-center text-lg font-semibold text-white transition-all hover:bg-purple-400 hover:shadow-lg"
          >
            Go Pro
          </a>

        </motion.div>
      </div>
    </div>
  </section>
  );
};

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-[#5eead4]/10">
    <button
      onClick={onClick}
      className="flex w-full items-center justify-between py-5 text-left"
    >
      <span className="text-lg font-medium text-white">{question}</span>
      <ChevronDown
        className={`h-5 w-5 text-[#5eead4] transition-transform duration-300 ${
          isOpen ? 'rotate-180' : ''
        }`}
      />
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <p className="pb-5 text-slate-400 leading-relaxed">{answer}</p>
    </motion.div>
  </div>
);

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What is Flow?",
      answer: "Flow is the complete AI marketing platform for elite real estate teams. It combines AI Virtual Staging, Video Reel creation, Batch Processing, Property Landing Pages, Agent Portfolios, and a Refer & Earn program—all in one platform. Everything you need to market properties faster and close more deals."
    },
    {
      question: "What AI tools are included?",
      answer: "Flow includes six powerful AI tools: Virtual Staging to transform empty rooms instantly, Video Reels to create TikTok/Instagram content in seconds, Batch Processing to handle entire photoshoots at once, Property Landing Pages for each listing, Agent Portfolio pages to showcase your brand, and Refer & Earn to grow your income by sharing Flow with your network."
    },
    {
      question: "How does AI Virtual Staging work?",
      answer: "Upload any empty room photo and our AI transforms it into a beautifully staged space in seconds. Choose from dozens of design styles—Modern, Scandinavian, Farmhouse, Luxury, and more. You can refine results using natural language, like 'add a plant in the corner' or 'change the sofa to blue.'"
    },
    {
      question: "What are Video Reels?",
      answer: "Video Reels automatically create engaging TikTok and Instagram-ready videos from your staged photos. Our AI generates smooth transitions, adds music, and optimizes for social media algorithms—helping you reach more buyers without any video editing skills."
    },
    {
      question: "How does Batch Processing save time?",
      answer: "Instead of staging photos one by one, Batch Processing lets you upload an entire photoshoot at once. Our AI detects room types automatically, applies consistent styling across all images, and processes everything in parallel—turning hours of work into minutes."
    },
    {
      question: "What are Property Landing Pages?",
      answer: "Each listing gets its own beautiful, shareable landing page with a custom URL. It showcases your staged photos, property details, and your agent branding. Perfect for social media, email campaigns, and direct buyer outreach."
    },
    {
      question: "What is the Agent Portfolio feature?",
      answer: "Your Agent Portfolio is a professional page showcasing all your listings in one place. It includes your branding, contact info, and links to each property landing page—essentially your digital storefront that builds credibility and generates leads."
    },
    {
      question: "How does the credit system work?",
      answer: "Credits power all AI features. HD photos cost 75 credits, 2K costs 150, and 4K costs 300. Video Reels and batch processing also use credits. Your credits refresh monthly, and annual plans get a 25% discount."
    },
    {
      question: "How does Refer & Earn work?",
      answer: "Share Flow with other agents and earn recurring commissions when they subscribe. It's a simple way to grow your income while helping colleagues discover tools that will transform their marketing."
    },
    {
      question: "Is there a free trial?",
      answer: "Yes! Start free with 5,000 credits (about 66 HD staged photos) to experience all features. No credit card required. When you're ready, upgrade to unlock more credits, video reels, batch processing, and team collaboration."
    }
  ];

  return (
    <section id="faq" className="bg-[#0a0f1a] px-6 py-24">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="text-sm font-semibold uppercase tracking-[0.24em] text-[#5eead4]">FAQ</div>
          <h2 className="mt-4 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl">
            Frequently Asked Questions
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-400">
            Everything you need to know about the complete AI marketing platform.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="rounded-2xl border border-[#5eead4]/10 bg-[#0d1420] px-6">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center">
          <p className="text-slate-400">
            Still have questions?{' '}
            <a href="mailto:support@cloudmortgage.io" className="text-[#5eead4] hover:underline">
              Contact our support team
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="border-t border-[#5eead4]/10 bg-[#0a0f1a] px-6 py-10 text-slate-400">
    <div className="mx-auto flex max-w-7xl flex-col gap-6 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-3">
        <img src={flowLogo} alt="Flow" className="h-10 w-10 rounded-xl" />
        <div>
          <div className="font-semibold text-white">Flow</div>
          <div className="text-sm text-[#5eead4]">Stage it. Share it. Sell it.</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-6 text-sm">
        <a href="/terms" className="transition-colors hover:text-white">Terms</a>
        <a href="/privacy" className="transition-colors hover:text-white">Privacy</a>
        <a href="/" className="transition-colors hover:text-white">CloudMortgage.io</a>
      </div>

      <div className="text-sm text-slate-500">
        &copy; {new Date().getFullYear()} CloudMortgage.io
      </div>
    </div>
  </footer>
);

const FlowLandingPage = () => {
  const [activeImage, setActiveImage] = useState(0);

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <Navigation />
      <HeroSection activeImage={activeImage} setActiveImage={setActiveImage} />
      <ProofBar />
      <WhyFlowSection />
      <ProofSection />
      <MakeWithFlowSection />
      <UserTestimonialsSection />
      <PricingSection />
      <FAQSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default FlowLandingPage;
